// Calibration-due bucketing for the Reports page.
//
// Pure date maths over the resource list — no API of its own. The
// catalogue is a few hundred rows, so grouping in the browser is
// cheaper than an endpoint, and the report stays correct the moment
// someone edits a due date.
//
// Two rules shape what the report counts, and both matter:
//
//   * Personnel are excluded. Calibration is an equipment property;
//     every person in the catalogue has no due date, so including them
//     would put the whole roster in a "not tracked" pile that never
//     empties.
//   * Retired equipment is excluded by default. Most of the past-due
//     backlog is scrapped kit — of the items sitting in the earliest
//     overdue quarter, nearly all are retired. Counting them makes the
//     report look alarming and tells you nothing actionable, so they
//     are opt-in.

import type { Resource } from './types';
import { addDays, parseIsoDate } from './format';

export interface Quarter {
  year: number;
  /** 1-4. */
  q: number;
  /** '2026-Q3' — stable key and sort order. */
  key: string;
  /** 'Q3 2026' */
  label: string;
  /** 'Jul–Sep' */
  months: string;
}

const MONTH_SPANS = ['Jan–Mar', 'Apr–Jun', 'Jul–Sep', 'Oct–Dec'];

export function quarterOf(iso: string): Quarter {
  const d = parseIsoDate(iso);
  return makeQuarter(d.getFullYear(), Math.floor(d.getMonth() / 3) + 1);
}

function makeQuarter(year: number, q: number): Quarter {
  return {
    year,
    q,
    key: `${year}-Q${q}`,
    label: `Q${q} ${year}`,
    months: MONTH_SPANS[q - 1],
  };
}

/** `count` quarters starting with the one containing `fromIso`. */
export function quartersFrom(fromIso: string, count: number): Quarter[] {
  const start = quarterOf(fromIso);
  const out: Quarter[] = [];
  for (let i = 0; i < count; i += 1) {
    // Quarters are 1-4, so shift into 0-based to carry the year over.
    const zero = start.q - 1 + i;
    out.push(makeQuarter(start.year + Math.floor(zero / 4), (zero % 4) + 1));
  }
  return out;
}

export interface CalibrationBucket {
  key: string;
  label: string;
  /** Secondary line — month span, or an explanation for the odd buckets. */
  detail: string;
  items: Resource[];
  /** Past due: render in red and never hide behind the horizon. */
  overdue?: boolean;
  /** A data gap rather than a schedule: no due date recorded. */
  untracked?: boolean;
}

export interface CalibrationReport {
  buckets: CalibrationBucket[];
  /** Due on or before `today + 30`, and not already overdue. */
  dueNext30: Resource[];
  overdue: Resource[];
  /** Tracked, active, in scope — the denominator for the counts above. */
  totalTracked: number;
  /** Due beyond the chosen horizon; counted so the total still adds up. */
  beyondHorizon: number;
}

export interface ReportOptions {
  today: string;
  /** How many quarters forward to show, starting with the current one. */
  quarters: number;
  includeRetired: boolean;
}

/**
 * Group equipment into overdue / per-quarter / untracked buckets.
 *
 * Dates are compared as YYYY-MM-DD strings, which sorts correctly and
 * sidesteps the timezone trap the rest of the app already avoids: every
 * date here is a local calendar day, and parsing one as UTC shifts it
 * across a quarter boundary at either end of the year.
 */
export function buildCalibrationReport(
  resources: Resource[],
  { today, quarters, includeRetired }: ReportOptions,
): CalibrationReport {
  const inScope = resources.filter(
    (r) => r.type === 'Equipment' && (includeRetired || r.isActive),
  );

  const overdue: Resource[] = [];
  const untracked: Resource[] = [];
  const dated: Resource[] = [];
  for (const r of inScope) {
    if (!r.nextDue) untracked.push(r);
    else if (r.nextDue < today) overdue.push(r);
    else dated.push(r);
  }

  const horizon = quartersFrom(today, quarters);
  const byQuarter = new Map<string, Resource[]>(horizon.map((q) => [q.key, []]));

  let beyondHorizon = 0;
  for (const r of dated) {
    const bucket = byQuarter.get(quarterOf(r.nextDue).key);
    if (bucket) bucket.push(r);
    else beyondHorizon += 1;
  }

  const byDue = (a: Resource, b: Resource) => a.nextDue.localeCompare(b.nextDue);
  const buckets: CalibrationBucket[] = [];

  if (overdue.length > 0) {
    buckets.push({
      key: 'overdue',
      label: 'Overdue',
      detail: 'Past due as of today',
      items: [...overdue].sort(byDue),
      overdue: true,
    });
  }

  for (const q of horizon) {
    buckets.push({
      key: q.key,
      label: q.label,
      detail: q.months,
      items: (byQuarter.get(q.key) ?? []).sort(byDue),
    });
  }

  if (untracked.length > 0) {
    buckets.push({
      key: 'untracked',
      label: 'No due date',
      detail: 'Not on a calibration schedule',
      items: [...untracked].sort((a, b) => a.title.localeCompare(b.title)),
      untracked: true,
    });
  }

  // Inclusive 30-day window, and only forward-looking: an overdue item
  // is already its own, louder, bucket and would be double-counted.
  const cutoff = addDays(today, 30);
  const dueNext30 = dated
    .filter((r) => r.nextDue <= cutoff)
    .sort(byDue);

  return {
    buckets,
    dueNext30,
    overdue: [...overdue].sort(byDue),
    totalTracked: overdue.length + dated.length,
    beyondHorizon,
  };
}
